import styled, { CSSObject } from '@emotion/styled';

import IconMap from '@/assets/icons/icon_map.svg';
import IconTel from '@/assets/icons/icon_tel.svg';
import IconWeb from '@/assets/icons/icon_web.svg';
import Compass0 from '@/assets/images/velodrome/img_compass_0.svg';
import Compass45 from '@/assets/images/velodrome/img_compass_45.svg';
import Compass90 from '@/assets/images/velodrome/img_compass_90.svg';
import Compass135 from '@/assets/images/velodrome/img_compass_135.svg';
import Compass180 from '@/assets/images/velodrome/img_compass_180.svg';
import Compass225 from '@/assets/images/velodrome/img_compass_225.svg';
import Compass270 from '@/assets/images/velodrome/img_compass_270.svg';
import Compass315 from '@/assets/images/velodrome/img_compass_315.svg';
import Perimeter250 from '@/assets/images/velodrome/img_perimeter_250.svg';
import Perimeter333 from '@/assets/images/velodrome/img_perimeter_333.svg';
import Perimeter335 from '@/assets/images/velodrome/img_perimeter_335.svg';
import Perimeter400 from '@/assets/images/velodrome/img_perimeter_400.svg';
import Perimeter500 from '@/assets/images/velodrome/img_perimeter_500.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { hoverOpacity } from '@/utility/theme/animation';

const mapCompassPath: { [key in number]: string } = {
  0: Compass0,
  45: Compass45,
  90: Compass90,
  135: Compass135,
  180: Compass180,
  225: Compass225,
  270: Compass270,
  315: Compass315,
};

const mapPerimeterPath: { [key in number]: string } = {
  250: Perimeter250,
  333: Perimeter333,
  335: Perimeter335,
  400: Perimeter400,
  500: Perimeter500,
};

const mapIconPath: { [key in 'tel' | 'map' | 'web']: string } = {
  tel: IconTel,
  map: IconMap,
  web: IconWeb,
};

export const Headline2 = styled.h2(() =>
  mq({
    width: '100%',
    fontSize: defaultTheme.font.size.l,
    fontWeight: 'bold',
    borderBottom: `1px solid ${defaultTheme.border.color.darkGray}`,
    paddingBottom: defaultTheme.spacing.s,
    marginBottom: [
      defaultTheme.spacing.l,
      defaultTheme.spacing.l,
      defaultTheme.spacing.xxl,
      defaultTheme.spacing.xxl,
    ],
  }),
);

export const BankImageWrapper = styled.div(
  (props: { freq: number; perimeter: number }) =>
    mq({
      position: 'relative',
      marginRight: ['0', '0', '40px', '40px'],
      '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: '0',
        left: '0',
        width: ['35px', '35px', '55px', '55px'],
        height: ['35px', '35px', '55px', '55px'],
        backgroundImage: `url(${mapCompassPath[props.freq]})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        zIndex: 2,
      },
      '&::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: ['74px', '74px', '125px', '125px'],
        left: ['54px', '54px', '87px', '87px'],
        width: ['105px', '105px', '190px', '190px'],
        height: ['24px', '24px', '42px', '42px'],
        backgroundImage: `url(${mapPerimeterPath[props.perimeter]})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        zIndex: 2,
      },
    }),
);

export const BankImage = styled.img(() =>
  mq({
    display: 'block',
    width: ['208px', '208px', '359.2px', '359.2px'],
    height: ['140px', '140px', '241.78px', '241.78px'],
  }),
);

export const Section = styled.div(() =>
  mq({
    width: '100%',
    maxWidth: '1280px',
    backgroundColor: defaultTheme.background.white,
    padding: [
      defaultTheme.spacing.l,
      defaultTheme.spacing.l,
      '32px',
      '32px 80px',
    ],
    marginBottom: [
      defaultTheme.spacing.xs,
      defaultTheme.spacing.xs,
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
    ],
  }),
);

export const BankInfo = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: ['column', 'column', 'row', 'row'],
    justifyContent: 'center',
    alignItems: ['center', 'center', 'inherit', 'inherit'],
    '& > i': {
      marginRight: '40px',
    },
  }),
);

export const BankDetail = styled.div(() =>
  mq({
    width: '100%',
    marginTop: [defaultTheme.spacing.l, defaultTheme.spacing.l, 0, 0],
  }),
);

export const Text = styled.p(() =>
  mq({
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
      defaultTheme.font.size.m,
    ],
    whiteSpace: 'pre-line',
  }),
);

export const BankDescription = styled(Text)(() =>
  mq({
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
      defaultTheme.font.size.m,
    ],
    marginBottom: [
      defaultTheme.spacing.l,
      defaultTheme.spacing.l,
      '32px',
      '32px',
    ],
  }),
);

export const TableWrapper = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: ['column', 'column', 'column', 'row'],
    '& > table:not(:first-of-type) tr:first-of-type': {
      borderTopWidth: [0, 0, 0, '1px'],
    },
  }),
);

export const Table = styled.table((props: { width?: CSSObject['width'] }) =>
  mq({
    width: props.width || '100%',
  }),
);

export const BankTable = styled(Table)(() =>
  mq({
    width: ['100%', '100%', '100%', 'calc(100% / 2)'],
  }),
);

export const TableRow = styled.tr(() =>
  mq({
    border: `solid ${defaultTheme.border.color.black}`,
    borderWidth: '1px 0',
  }),
);

export const TableHeader = styled.th((props: { width?: CSSObject['width'] }) =>
  mq({
    width: props.width || 'auto',
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
    ],
    fontWeight: 'normal',
    verticalAlign: 'top',
    textAlign: 'left',
    backgroundColor: defaultTheme.background.mediumGray,
    padding: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      `${defaultTheme.spacing.m} ${defaultTheme.spacing.l}`,
    ],
  }),
);

export const BankTableHeader = styled(TableHeader)(() =>
  mq({
    width: ['150px', '150px', '176px'],
  }),
);

export const AccessTableHeader = styled(TableHeader)(() =>
  mq({
    width: ['108px', '108px', '208px'],
  }),
);

export const FeeTableHeader = styled(TableHeader)(() =>
  mq({
    width: ['108px', '108px', '208px'],
  }),
);

export const TableData = styled.td(() =>
  mq({
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
    ],
    verticalAlign: 'middle',
    padding: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.m,
    ],
  }),
);

export const AccessFields = styled.div(() =>
  mq({
    '& > div:not(:first-of-type)': {
      borderBottom: `1px solid ${defaultTheme.border.color.lightGray}`,
      padding: `${defaultTheme.spacing.m} 0`,
    },
  }),
);

export const AccessField = styled.div(
  (props: { icon: 'map' | 'tel' | 'web' }) =>
    mq({
      display: 'grid',
      gridTemplateColumns: '14px 1fr',
      gridGap: defaultTheme.spacing.s,
      alignItems: 'center',
      fontSize: [
        defaultTheme.font.size.s,
        defaultTheme.font.size.s,
        defaultTheme.font.size.m,
      ],
      '&::before': {
        content: '""',
        display: 'inline-block',
        width: '14px',
        height: '14px',
        backgroundImage: `url(${mapIconPath[props.icon]})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
      },
    }),
);

export const AnchorLink = styled.a(() =>
  mq({
    textDecorationLine: 'underline',
    ...hoverOpacity,
  }),
);
